"use client";

import { c as _c } from "react/compiler-runtime";
import { Accordion as HeroAccordion, AccordionItem as HeroAccordionItem } from "@heroui/react";
import type React from "react";
import { cn } from "../../lib/utils";

// Get the original component props types
export type AccordionProps = React.ComponentProps<typeof HeroAccordion> & {
  className?: string;
  itemClasses?: React.ComponentProps<typeof HeroAccordion>["itemClasses"];
};
export type AccordionItemProps = React.ComponentProps<typeof HeroAccordionItem> & {
  className?: string;
};

// Create the wrapper but preserve the original component's identity
const AccordionWrapper = t0 => {
  const $ = _c(14);
  let children;
  let className;
  let itemClasses;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      itemClasses,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = itemClasses;
    $[4] = props;
  } else {
    children = $[1];
    className = $[2];
    itemClasses = $[3];
    props = $[4];
  }
  let t1;
  if ($[5] !== itemClasses) {
    const defaultItemClasses = {
      indicator: "text-icon-neutral-onMatch-usual! [&_svg]:size-6",
      trigger: "hover:cursor-pointer"
    };
    t1 = {
      ...defaultItemClasses,
      ...itemClasses,
      indicator: cn(defaultItemClasses.indicator, itemClasses?.indicator)
    };
    $[5] = itemClasses;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const mergedItemClasses = t1;
  let t2;
  if ($[7] !== className) {
    t2 = cn(className);
    $[7] = className;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== children || $[10] !== mergedItemClasses || $[11] !== props || $[12] !== t2) {
    t3 = <HeroAccordion {...props} className={t2} itemClasses={mergedItemClasses}>{children}</HeroAccordion>;
    $[9] = children;
    $[10] = mergedItemClasses;
    $[11] = props;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
};
const AccordionItemWrapper = t0 => {
  const $ = _c(20);
  let children;
  let className;
  let classNames;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      classNames,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = classNames;
    $[4] = props;
  } else {
    children = $[1];
    className = $[2];
    classNames = $[3];
    props = $[4];
  }
  let t1;
  let t2;
  let t3;
  if ($[5] !== classNames) {
    const defaultClassNames = {
      indicator: "text-icon-neutral-onMatch-usual! [&_svg]:size-6",
      trigger: "hover:cursor-pointer"
    };
    t1 = classNames;
    t2 = cn(defaultClassNames.indicator, classNames?.indicator);
    t3 = cn(defaultClassNames.trigger, classNames?.trigger);
    $[5] = classNames;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t1 = $[6];
    t2 = $[7];
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t1 || $[10] !== t2 || $[11] !== t3) {
    t4 = {
      ...t1,
      indicator: t2,
      trigger: t3
    };
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const mergedClassNames = t4;
  let t5;
  if ($[13] !== className) {
    t5 = cn(className);
    $[13] = className;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== children || $[16] !== mergedClassNames || $[17] !== props || $[18] !== t5) {
    t6 = <HeroAccordionItem {...props} className={t5} classNames={mergedClassNames}>{children}</HeroAccordionItem>;
    $[15] = children;
    $[16] = mergedClassNames;
    $[17] = props;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  return t6;
};

// Copy all static properties from the original components
Object.setPrototypeOf(AccordionWrapper, HeroAccordion);
Object.setPrototypeOf(AccordionItemWrapper, HeroAccordionItem);

// Copy static properties that HeroUI's collection system might need
Object.assign(AccordionWrapper, HeroAccordion);
Object.assign(AccordionItemWrapper, HeroAccordionItem);

// Ensure the components have the right displayName and type
(AccordionWrapper as unknown as {
  displayName?: string;
}).displayName = (HeroAccordion as unknown as {
  displayName?: string;
}).displayName || "Accordion";
(AccordionItemWrapper as unknown as {
  displayName?: string;
}).displayName = (HeroAccordionItem as unknown as {
  displayName?: string;
}).displayName || "AccordionItem";

// Copy the $$typeof symbol if it exists (React uses this for component identification)
if ("$$typeof" in HeroAccordion) {
  (AccordionWrapper as unknown as {
    $$typeof?: symbol;
  }).$$typeof = (HeroAccordion as unknown as {
    $$typeof?: symbol;
  }).$$typeof;
}
if ("$$typeof" in HeroAccordionItem) {
  (AccordionItemWrapper as unknown as {
    $$typeof?: symbol;
  }).$$typeof = (HeroAccordionItem as unknown as {
    $$typeof?: symbol;
  }).$$typeof;
}
export const Accordion = AccordionWrapper;
export const AccordionItem = AccordionItemWrapper;